// import NProgress from 'nprogress' // progress bar
// import 'nprogress/nprogress.css' // progress bar style
import getPageTitle from '@/utils/get-page-title'
import { usePermissionStore } from '@/stores/modules/permission'
import { useUserStore } from '@/stores/modules/user'
import { useMerchantStore } from '@/stores/modules/merchant'
import { useStaffStore } from '@/stores/modules/staff'
import { useMerchantDeviceStore } from '@/stores/modules/merchant-device'
import { usePayMethodStore } from '@/stores/modules/pay-method'
import { useMerchantDeviceBinding } from '@/hooks/useMerchantDeviceBinding'
import { getToken } from '@/utils/auth'
import { asyncRoutes } from '@/router/index.js'

// NProgress.configure({ showSpinner: false }) // NProgress Configuration

// 路由白名单
const whiteList = ['/login'] // no redirect whitelist

/**
 * 通过路由的路径判断当前的路由作用域
 */
function changeRouterScopeByPath(path, asyncRoutes) {
  // 修改顶部菜单栏
  const permissionStore = usePermissionStore()
  const routerScope = getScope(path, asyncRoutes) || permissionStore.routerScope // 如果未匹配到，则重置为默认的路由作用域
  permissionStore.changeRouterScope({ scope: routerScope })
}

/**
 * 基于角色、商家类型、路由isHiddenRoute配置，
 * 过滤出可访问的菜单，并注册到 Vue Router
 */
async function dynamicAddRoute(router) {
  const userStore = useUserStore()
  const { rolePermission } = await userStore.getInfo()
  const permissionStore = usePermissionStore()
  removeAllAsyncRoutes(router) // 先移除所有路由
  const accessRoutes = permissionStore.generateRoutes({ rolePermission })
  if (accessRoutes && accessRoutes.length) {
    accessRoutes.forEach((accessRoute) => {
      router.addRoute(accessRoute)
    })
  }
  // console.log('注册路由', accessRoutes, router.getRoutes())
  return accessRoutes
}

/**
 * 先移除所有路由
 * @param router
 */
function removeAllAsyncRoutes(router) {
  try {
    asyncRoutes.forEach((route) => {
      if (route.name) {
        router.removeRoute(route.name)
      }
    })
  } catch (e) {
    console.log('移除所有路由失败', e)
  }
}

/**
 * 判断要跳转的路由路径属于哪个路由作用域
 * @param path 路由路径
 * @param routers 路由配置列表
 * @returns {string}
 */
function getScope(path, routers) {
  let scope = ''
  for (const item of routers) {
    if (item.formatted_full_path && path === item.formatted_full_path) {
      scope = item.scope
      break
    }

    if (item.children) {
      scope = getScope(path, item.children)
      if (scope) {
        break
      }
    }
  }
  return scope
}

/**
 * 获取商户信息
 */
const getMerchantInfo = async () => {
  const merchantStore = useMerchantStore()
  if (merchantStore.merchantInfo) {
    return
  }
  await merchantStore.getMerchantInfo()
}

/**
 * 获取登录员工信息
 */
const getStaffInfo = async (to) => {
  if (whiteList.indexOf(to.path) !== -1) {
    return
  }

  const staffStore = useStaffStore()
  if (!staffStore.staffInfo) {
    await staffStore.getStaffInfo()
  }
}

/**
 * 获取当前设备详情
 */
const getMerchantDeviceDetail = async (to) => {
  if (whiteList.indexOf(to.path) !== -1) {
    return
  }

  const merchantDeviceStore = useMerchantDeviceStore()
  if (!merchantDeviceStore.merchantDevice) {
    await merchantDeviceStore.getMerchantDeviceDetail()
  }
}

/**
 * 获取自定义线下支付方式
 */
const getCustomOfflinePayMethod = (to) => {
  if (whiteList.indexOf(to.path) !== -1) {
    return
  }
  const payMethodStore = usePayMethodStore()
  const customOfflinePayMethods = payMethodStore.customOfflinePayMethods
  if (!customOfflinePayMethods) {
    payMethodStore.getCustomOfflinePayMethods()
  }
}

/**
 * 隐藏app loading状态
 */
function hideAppLoading() {
  document.getElementById('app').style.display = 'block'
  document.getElementById('app-loading').style.display = 'none'
}

export const setupRoutePermission = function (router, asyncRoutes) {
  /**
   * 注册 Vue Router beforeEach 钩子函数
   * 拦截处理权限和路由判断等事情
   * https://router.vuejs.org/zh/guide/advanced/navigation-guards.html#全局前置守卫
   */
  const { checkIsBinding } = useMerchantDeviceBinding()
  router.beforeEach(async (to) => {
    // console.log('beforeEach', to)
    // start progress bar
    // NProgress.start()

    document.title = getPageTitle(to.meta.title)

    // 防止重复跳转
    if (to.path === '/merchant-device-binding' || to.path === '/network-error') {
      hideAppLoading()
      return
    }
    const isBinding = await checkIsBinding()
    // 当前设备没有绑定门店，重定向到设备绑定页
    if (!isBinding) {
      hideAppLoading()
      return { path: '/merchant-device-binding', replace: true }
    }

    const hasToken = getToken()
    if (hasToken) {
      // 用户已登录，但是访问的是 /login，直接跳到首页
      if (to.path === '/login') {
        hideAppLoading()
        return { path: '/', replace: true }
      }

      // set page title
      getCustomOfflinePayMethod(to)

      // 判断用户是否有权限访问访问访问
      const userStore = useUserStore()
      const hasPermissions = userStore.rolePermission

      if (hasPermissions) {
        // 通过路由的路径判断当前的路由作用域
        changeRouterScopeByPath(to.path, asyncRoutes)
        hideAppLoading()
        return true
      } else {
        try {
          // 获取商户信息
          await getMerchantInfo()
          const [staffRes, accessRoutes] = await Promise.all([
            // 获取员工信息
            getStaffInfo(to),

            // 注册路由
            dynamicAddRoute(router),

            getMerchantDeviceDetail(to)
          ])
          hideAppLoading()

          // 添加首页路由
          // 没指定路径, 自动跳转到有权限的第一个页面
          if (to.path === '/') {
            const firstRoute = accessRoutes[0]?.children?.[0]
            if (!firstRoute) {
              return
            }
            // console.log('动态添加路由不能return true，需要明确导航到一个路由', to, firstRoute)
            return {
              path: firstRoute.formatted_full_path,
              replace: true,
              query: { t: Date.now() } // 加上时间戳，否则如果当前页面是firstRoute，不会触发页面组件内部的生命周期函数
            } // 动态添加路由不能return true，需要明确导航到一个路由
          }
          return { ...to, replace: true } // 直接返回目标路由对象，保留所有路由信息
        } catch (error) {
          // console.log('error: ', error)
          //
          // // 清除登录态信息
          // const merchantStore = useMerchantStore()
          // const userStore = useUserStore()
          // userStore.resetToken()
          // merchantStore.clearMerchantInfo()
          //
          // // 跳转登录页
          // return { path: `/login?redirect=${to.path}`, replace: true }
          hideAppLoading()
          return { path: '/network-error', replace: true }
        }
      }
    } else {
      hideAppLoading()
      /* has no token*/
      if (whiteList.indexOf(to.path) !== -1) {
        return true
      } else {
        return { path: `/login?redirect=${to.path}`, replace: true }
      }
    }
  })

  // router.afterEach(() => {
  // finish progress bar
  // NProgress.done()
  // })
}
